import { z } from 'zod';
import {
  atualizarPessoaSchema,
  contribuinteIcmsSchema,
  criarPessoaSchema,
  documentoValido,
  soDigitos,
  TIPOS_ENDERECO,
  type AtualizarPessoaDto,
  type ContatoParceiroDto,
  type CriarPessoaDto,
  type EnderecoParceiroDto,
  type PessoaResponse,
} from '@triade/contracts';
import type { EnderecoCampo } from '@/components/parceiro';

/**
 * Formulario da pessoa. A identidade, o fiscal, as observacoes, os enderecos e os contatos sao do PARCEIRO (componente ParceiroBlocos);
 * aqui ficam os campos proprios da pessoa (colaborador). Strings de tela; o DTO sai pelo contrato (criarPessoaSchema / atualizarPessoaSchema).
 */

export const NATUREZA_ROTULO = { FISICA: 'Física', JURIDICA: 'Jurídica' } as const;
/** Turno do colaborador (rotulos da tela). '' = sem turno. */
export const TURNO_ROTULO = { '': 'Não informado', '1': '1º turno', '2': '2º turno', '3': '3º turno', COMERCIAL: 'Comercial' } as const;
/** Aviso exibido ao informar a demissao (a API encerra a vigencia dos parametros de trabalho). */
export const AVISO_DEMISSAO = 'A demissão encerra a vigência dos parâmetros de trabalho.';
/** Papeis do parceiro que aparecem como chips na pessoa (leitura). */
export const PAPEIS_DA_PESSOA = ['COLABORADOR', 'TERCEIRIZADO'] as const;

const texto = (max: number, mensagem?: string) => z.string().trim().max(max, mensagem ?? `Máximo de ${max} caracteres`);

const enderecoFormSchema = z.object({
  tipo: z.enum(TIPOS_ENDERECO),
  principal: z.boolean(),
  cep: z.string(),
  logradouro: z.string(),
  numero: z.string(),
  complemento: z.string(),
  bairro: z.string(),
  municipio: z.string(),
  uf: z.string(),
  codigoIbge: z.string(),
});

const contatoFormSchema = z.object({
  nome: z.string(),
  cargo: z.string(),
  email: z.string(),
  telefone: z.string(),
  /** Nao aparece na tela (o bloco de contatos do parceiro nao o mostra); o bloco novo nao o traz, e o que veio da API volta sem alteracao. */
  observacao: z.string().optional(),
});

export const pessoaFormSchema = z
  .object({
    natureza: z.enum(['FISICA', 'JURIDICA'], { message: 'Escolha a natureza' }),
    nome: z.string().trim().min(1, 'Informe o nome').max(200, 'O nome tem no máximo 200 caracteres'),
    fantasia: texto(200),
    documento: z.string(),
    ie: texto(30),
    contribuinteIcms: z.union([z.literal(''), contribuinteIcmsSchema]),
    observacoes: texto(2000),
    enderecos: z.array(enderecoFormSchema).max(10, 'No máximo 10 endereços'),
    contatos: z.array(contatoFormSchema).max(20, 'No máximo 20 contatos'),
    identificador: texto(50),
    nascimento: z.string(),
    departamento: texto(100),
    turno: z.enum(['', '1', '2', '3', 'COMERCIAL']),
    admissao: z.string(),
    demissao: z.string(),
    descricao: texto(500),
    ativo: z.boolean(),
  })
  .superRefine((v, ctx) => {
    const doc = soDigitos(v.documento);
    if (doc && doc.length !== 11 && doc.length !== 14) ctx.addIssue({ code: 'custom', path: ['documento'], message: 'Informe 11 dígitos (CPF) ou 14 (CNPJ)' });
    else if (doc && !documentoValido(doc)) ctx.addIssue({ code: 'custom', path: ['documento'], message: 'CPF ou CNPJ inválido (confira os dígitos)' });
    if (v.nascimento && !z.iso.date().safeParse(v.nascimento).success) {
      ctx.addIssue({ code: 'custom', path: ['nascimento'], message: 'Data inválida' });
    }
    for (const campo of ['admissao', 'demissao'] as const) {
      if (v[campo] && !z.iso.date().safeParse(v[campo]).success) ctx.addIssue({ code: 'custom', path: [campo], message: 'Data inválida' });
    }
  });

/** Valores do formulario: os campos do parceiro (ParceiroCampos) mais os proprios da pessoa. */
export type PessoaFormEntrada = z.input<typeof pessoaFormSchema>;
export type PessoaFormSaida = z.output<typeof pessoaFormSchema>;
export type EnderecoForm = EnderecoCampo;
export type ContatoForm = PessoaFormSaida['contatos'][number];

/** Cadastro em branco (pessoa nova entra ativa; natureza fisica). */
export function pessoaFormVazio(): PessoaFormEntrada {
  return {
    natureza: 'FISICA',
    nome: '',
    fantasia: '',
    documento: '',
    ie: '',
    contribuinteIcms: '',
    observacoes: '',
    enderecos: [],
    contatos: [],
    identificador: '',
    nascimento: '',
    departamento: '',
    turno: '',
    admissao: '',
    demissao: '',
    descricao: '',
    ativo: true,
  };
}

const opt = (s: string): string | undefined => {
  const t = s.trim();
  return t === '' ? undefined : t;
};

/** Registro da API para o formulario (documento so digitos, data so AAAA-MM-DD). Valores vem prontos da API; nada e calculado aqui. */
export function dePessoa(p: PessoaResponse): PessoaFormEntrada {
  return {
    natureza: p.natureza,
    nome: p.nome,
    fantasia: p.fantasia ?? '',
    documento: p.documento ?? '',
    ie: p.ie ?? '',
    contribuinteIcms: p.contribuinteIcms ?? '',
    observacoes: p.observacoes ?? '',
    enderecos: p.enderecos.map((e) => ({
      tipo: e.tipo,
      principal: e.principal,
      cep: e.cep ?? '',
      logradouro: e.logradouro ?? '',
      numero: e.numero ?? '',
      complemento: e.complemento ?? '',
      bairro: e.bairro ?? '',
      municipio: e.municipio ?? '',
      uf: e.uf ?? '',
      codigoIbge: e.codigoIbge ?? '',
    })),
    contatos: p.contatos.map((c) => ({
      nome: c.nome,
      cargo: c.cargo ?? '',
      email: c.email ?? '',
      telefone: c.telefone ?? '',
      observacao: c.observacao ?? '',
    })),
    identificador: p.identificador ?? '',
    nascimento: p.nascimento ? p.nascimento.slice(0, 10) : '',
    departamento: p.departamento ?? '',
    turno: p.turno ?? '',
    admissao: p.admissao ? p.admissao.slice(0, 10) : '',
    demissao: p.demissao ? p.demissao.slice(0, 10) : '',
    descricao: p.descricao ?? '',
    ativo: p.ativo,
  };
}

function enderecoDto(e: EnderecoForm): EnderecoParceiroDto {
  const cep = soDigitos(e.cep);
  const uf = opt(e.uf);
  const logradouro = opt(e.logradouro);
  const numero = opt(e.numero);
  const complemento = opt(e.complemento);
  const bairro = opt(e.bairro);
  const municipio = opt(e.municipio);
  const codigoIbge = opt(e.codigoIbge);
  return {
    tipo: e.tipo,
    principal: e.principal,
    ...(cep ? { cep } : {}),
    ...(logradouro ? { logradouro } : {}),
    ...(numero ? { numero } : {}),
    ...(complemento ? { complemento } : {}),
    ...(bairro ? { bairro } : {}),
    ...(municipio ? { municipio } : {}),
    ...(codigoIbge ? { codigoIbge } : {}),
    ...(uf ? { uf: uf.toUpperCase() } : {}),
  };
}

function contatoDto(c: ContatoForm): ContatoParceiroDto {
  const cargo = opt(c.cargo);
  const email = opt(c.email);
  const telefone = opt(c.telefone);
  const observacao = opt(c.observacao ?? '');
  return {
    nome: c.nome.trim(),
    ...(cargo ? { cargo } : {}),
    ...(email ? { email } : {}),
    ...(telefone ? { telefone } : {}),
    ...(observacao ? { observacao } : {}),
  };
}

/** DTO de criacao: campos vazios somem. Passa pelo contrato (criarPessoaSchema); erro devolve a mensagem. */
export function paraCriar(v: PessoaFormSaida): { ok: true; dto: CriarPessoaDto } | { ok: false; mensagem: string } {
  const doc = soDigitos(v.documento);
  const fantasia = opt(v.fantasia);
  const ie = opt(v.ie);
  const observacoes = opt(v.observacoes);
  const identificador = opt(v.identificador);
  const departamento = opt(v.departamento);
  const descricao = opt(v.descricao);
  const dto = {
    natureza: v.natureza,
    nome: v.nome.trim(),
    ...(doc ? { documento: doc } : {}),
    ...(fantasia ? { fantasia } : {}),
    ...(ie ? { ie } : {}),
    ...(v.contribuinteIcms ? { contribuinteIcms: v.contribuinteIcms } : {}),
    ...(observacoes ? { observacoes } : {}),
    ...(identificador ? { identificador } : {}),
    ...(v.nascimento ? { nascimento: v.nascimento } : {}),
    ...(departamento ? { departamento } : {}),
    ...(v.turno ? { turno: v.turno } : {}),
    ...(v.admissao ? { admissao: v.admissao } : {}),
    ...(v.demissao ? { demissao: v.demissao } : {}),
    ...(descricao ? { descricao } : {}),
    enderecos: v.enderecos.map(enderecoDto),
    contatos: v.contatos.map(contatoDto),
  };
  const r = criarPessoaSchema.safeParse(dto);
  return r.success ? { ok: true, dto: r.data } : { ok: false, mensagem: r.error.issues[0]?.message ?? 'Dados inválidos' };
}

/**
 * DTO de alteracao: envia a identidade e os enderecos/contatos inteiros; vazio vira null (limpa). Documento e nascimento tambem.
 * Os campos de RH (departamento, turno, admissao, demissao) so vao quando alterados (`alterados`); vazio vira null (demissao null limpa).
 */
export function paraAtualizar(v: PessoaFormSaida, alterados: ReadonlySet<string> = new Set()): { ok: true; dto: AtualizarPessoaDto } | { ok: false; mensagem: string } {
  const doc = soDigitos(v.documento);
  const rh: Record<string, string | null> = {};
  if (alterados.has('departamento')) rh.departamento = opt(v.departamento) ?? null;
  if (alterados.has('turno')) rh.turno = v.turno === '' ? null : v.turno;
  if (alterados.has('admissao')) rh.admissao = v.admissao ? v.admissao : null;
  if (alterados.has('demissao')) rh.demissao = v.demissao ? v.demissao : null;
  const dto = {
    natureza: v.natureza,
    nome: v.nome.trim(),
    fantasia: opt(v.fantasia) ?? null,
    ie: opt(v.ie) ?? null,
    contribuinteIcms: v.contribuinteIcms || null,
    observacoes: opt(v.observacoes) ?? null,
    identificador: v.identificador.trim(),
    documento: doc ? doc : null,
    nascimento: v.nascimento ? v.nascimento : null,
    descricao: v.descricao.trim(),
    ativo: v.ativo,
    enderecos: v.enderecos.map(enderecoDto),
    contatos: v.contatos.map(contatoDto),
    ...rh,
  };
  const r = atualizarPessoaSchema.safeParse(dto);
  return r.success ? { ok: true, dto: r.data } : { ok: false, mensagem: r.error.issues[0]?.message ?? 'Dados inválidos' };
}
